import React from 'react';
import { PERMISSIONS } from 'constants/permissions';
import { ValidationErrors } from 'constants/validations';
import { ContactPersonAddEdit } from 'components/common/contactPerson/addEdit';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { Col, Form, FormModal, Input, Row, Select, SupplierSelect, Typography, Upload } from 'components';
import { useAddSupplierOffer } from './useAddSupplierOffer';
import { AttachmentForm } from './../edit/attachments';

const { Text } = Typography;

export interface IAddSupplierOfferModal {
  modalState: boolean;
  onModalStateChange: () => void;
  id: string;
}

export const AddSupplierOfferModal: React.FC<IAddSupplierOfferModal> = (props) => {
  const { onModalStateChange, modalState, id } = props;
  const { hasAccess } = useAccessControl();
  const {
    onFinish,
    form,
    onCancel,
    isLoading,
    handleFormChange,
    isLoadingContactPerson,
    contactPersons,
    selectedSupplier,
    openContactPersonModal,
    attachments,
    handleEmailUpload,
    setAttachments,
    setOpenContactPersonModal,
    onEmailRemove,
    fileList,
    setFileList,
    attachmentType,
    setAttachmentType,
  } = useAddSupplierOffer({ onModalStateChange, id });

  return (
    <>
      <FormModal
        onValuesChange={handleFormChange}
        form={form}
        title={'Add Supplier Offer'}
        okText={'Add'}
        open={modalState}
        isLoading={isLoading}
        onCancel={onCancel}
        onSubmit={onFinish}
      >
        <Row gutter={[0, 20]}>
          <Col span={24}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  name="emailFile"
                  dependencies={['source']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const source = getFieldValue('source');
                        if (source === 'EMAIL' && (!value || value.length === 0)) {
                          return Promise.reject(ValidationErrors.required('Email'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  getValueFromEvent={(e) => e?.fileList}
                >
                  <Upload.Dragger
                    accept=".eml"
                    className={'email-upload'}
                    beforeUpload={handleEmailUpload}
                    maxCount={1}
                    onRemove={onEmailRemove}
                  >
                    <Row style={{ padding: '34px' }} justify={'center'}>
                      <Text className={'font-size-18 font-weight-700'}>Upload Email</Text>
                    </Row>
                  </Upload.Dragger>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="supplierId"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Supplier'),
                    },
                  ]}
                  label={'Supplier*'}
                >
                  <SupplierSelect size={'large'} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="source"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Source'),
                    },
                  ]}
                  label={'Source*'}
                >
                  <Select
                    options={[
                      { value: 'EMAIL', label: 'Email' },
                      { value: 'PRICE_LIST', label: 'Price list' },
                    ]}
                    size={'large'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="contactPersonId"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Contact Person'),
                    },
                  ]}
                  label={'Contact Person*'}
                >
                  <Select
                    size={'large'}
                    loading={isLoadingContactPerson}
                    onAdd={() => setOpenContactPersonModal(true)}
                    showAdd={hasAccess(PERMISSIONS.clients.contact_person.write)}
                    disabled={!selectedSupplier}
                    options={contactPersons}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="offerNumber"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Supplier Offer Number'),
                    },
                  ]}
                  label={'Supplier Offer Number*'}
                >
                  <Input size={'large'} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <AttachmentForm
                  attachments={attachments}
                  setAttachments={setAttachments}
                  fileList={fileList}
                  setFileList={setFileList}
                  attachmentType={attachmentType}
                  setAttachmentType={setAttachmentType}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </FormModal>
      <ContactPersonAddEdit
        entityName={'SUPPLIER'}
        modalState={openContactPersonModal}
        clientId={selectedSupplier}
        onModalStateChange={() => setOpenContactPersonModal((oState) => !oState)}
      />
    </>
  );
};
